import React, { useState, useEffect, useMemo } from "react";
import { ArrowUpDown } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { useUser } from "../contexts/UserContext";

const ArchivedTable = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [archivedApps, setArchivedApps] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "netTotal",
    direction: "desc",
  });
  const { user } = useUser();

  // Fetch archived apps and transactions
  const fetchData = async () => {
    try {
      if (!user?.id) return;

      const [appsResponse, transactionsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}apps/archived/${user.id}`),
        fetch(`${API_BASE_URL}transactions/${user.id}`),
      ]);

      if (appsResponse.ok && transactionsResponse.ok) {
        const [appsData, transactionsData] = await Promise.all([
          appsResponse.json(),
          transactionsResponse.json(),
        ]);
        setArchivedApps(appsData);
        setTransactions(transactionsData);
      }
    } catch (error) {
      console.error("Error fetching archived data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleUnarchive = async (appId) => {
    try {
      const response = await fetch(`${API_BASE_URL}apps/${appId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive: true }),
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error("Error unarchiving app:", error);
    }
  };

  // Calculate totals from transactions
  const appTotals = useMemo(() => {
    const totals = {};

    transactions.forEach((transaction) => {
      const appId = transaction.appId._id || transaction.appId;
      if (!totals[appId]) {
        totals[appId] = {
          totalDeposit: 0,
          totalWithdraw: 0,
        };
      }

      if (transaction.type === "deposit") {
        totals[appId].totalDeposit += Math.abs(transaction.amount);
      } else {
        totals[appId].totalWithdraw += Math.abs(transaction.amount);
      }
    });

    return totals;
  }, [transactions]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for calculated fields
      if (sortConfig.key === "netTotal") {
        const aTotal = appTotals[a._id] || { totalDeposit: 0, totalWithdraw: 0 };
        const bTotal = appTotals[b._id] || { totalDeposit: 0, totalWithdraw: 0 };
        aValue = aTotal.totalWithdraw - aTotal.totalDeposit;
        bValue = bTotal.totalWithdraw - bTotal.totalDeposit;
      } else if (sortConfig.key === "totalDeposit") {
        aValue = (appTotals[a._id] || { totalDeposit: 0 }).totalDeposit;
        bValue = (appTotals[b._id] || { totalDeposit: 0 }).totalDeposit;
      } else if (sortConfig.key === "totalWithdraw") {
        aValue = (appTotals[a._id] || { totalWithdraw: 0 }).totalWithdraw;
        bValue = (appTotals[b._id] || { totalWithdraw: 0 }).totalWithdraw;
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        } cursor-pointer`}
      />
    );
  };

  const sortedArchivedApps = useMemo(() => {
    return getSortedData(archivedApps);
  }, [archivedApps, sortConfig, appTotals]);

  return (
    <Card className="mt-8" style={{ backgroundColor: "#FFFFFF" }}>
      <CardHeader>
        <CardTitle>Archived Casinos</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-2 sm:p-3 text-left font-medium">Action</th>
                <th
                  className="p-2 sm:p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("name")}
                >
                  Name {renderSortIcon("name")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("totalDeposit")}
                >
                  Total Deposit {renderSortIcon("totalDeposit")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("totalWithdraw")}
                >
                  Total Withdraw {renderSortIcon("totalWithdraw")}
                </th>
                <th
                  className="p-2 sm:p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("netTotal")}
                >
                  Net Total {renderSortIcon("netTotal")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedArchivedApps.map((app) => {
                const appTotal = appTotals[app._id] || {
                  totalDeposit: 0,
                  totalWithdraw: 0,
                };
                const netTotal = appTotal.totalWithdraw - appTotal.totalDeposit;

                return (
                  <tr key={app._id} className="border-t">
                    <td className="p-3">
                      <button
                        onClick={() => handleUnarchive(app._id)}
                        className="px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors text-sm"
                      >
                        Restore
                      </button>
                    </td>
                    <td className="p-3">
                      <span className="text-gray-900">{app.name}</span>
                    </td>
                    <td className="p-3 text-right text-red-600">
                      ${appTotal.totalDeposit.toFixed(2)}
                    </td>
                    <td className="p-3 text-right text-green-600">
                      ${appTotal.totalWithdraw.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-bold">
                      ${netTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {sortedArchivedApps.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-gray-500">
                    No archived casinos found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Total Archived: {archivedApps.length}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArchivedTable;